const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const REQUEST_TIMEOUT_MS = 60_000;

export function apiUrl(path: string): string {
	return `${API_BASE}${path}`;
}

function getCsrfToken(): string | null {
	const match = document.cookie.match(/(?:^|; )csrf_token=([^;]*)/);
	return match?.[1] ?? null;
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		...(init?.headers as Record<string, string>),
	};

	if (init?.method && init.method !== "GET" && init.method !== "HEAD") {
		const csrf = getCsrfToken();
		if (csrf) {
			headers["x-csrf-token"] = csrf;
		}
	}

	let res: Response;
	try {
		res = await fetch(`${API_BASE}${path}`, {
			...init,
			headers,
			credentials: "include",
			signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
		});
	} catch (err) {
		if (err instanceof DOMException && err.name === "TimeoutError") {
			throw new Error("İstek zaman aşımına uğradı. Lütfen tekrar deneyin.", { cause: err });
		}
		throw err;
	}
	if (!res.ok) {
		const err = await res.json().catch(() => ({ error: "Request failed" }));
		throw new Error(err.error ?? "Request failed");
	}
	if (res.status === 204 || res.headers.get("content-length") === "0") {
		return undefined as T;
	}
	return res.json() as Promise<T>;
}

export const api = {
	get: <T>(path: string) => fetchJson<T>(path, { method: "GET" }),
	post: <T>(path: string, body: unknown) =>
		fetchJson<T>(path, { method: "POST", body: JSON.stringify(body) }),
	put: <T>(path: string, body: unknown) =>
		fetchJson<T>(path, { method: "PUT", body: JSON.stringify(body) }),
	patch: <T>(path: string, body: unknown) =>
		fetchJson<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
	delete: <T>(path: string) => fetchJson<T>(path, { method: "DELETE" }),
	upload: async <T>(path: string, formData: FormData): Promise<T> => {
		const headers: Record<string, string> = {};
		const csrf = getCsrfToken();
		if (csrf) headers["x-csrf-token"] = csrf;
		const res = await fetch(`${API_BASE}${path}`, {
			method: "POST",
			body: formData,
			headers,
			credentials: "include",
		});
		if (!res.ok) {
			const err = await res.json().catch(() => ({ error: "Request failed" }));
			throw new Error(err.error ?? "Request failed");
		}
		return res.json() as Promise<T>;
	},
};
